
import { APIErrorFormat } from '@/types/Errors'
import Vue from 'vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { PASSWORD_REGEXP } from '@/types/RegExpressions'
import { RecoveryReasons } from '@/types/Login'
import router from '@/router'
import LogService from '@/services/support/LogService'
import { DomainSecurityLevel } from '@/types/Catalogs'
import RecoveryPasswordActions from '@/services/RecoveryPassword'
import { RecoveryPasswordDetails } from '@/types/RecoveryPassword'
import PasswordDetails from '@/components/generic/PasswordDetails.vue'

@Component({
  name: 'RecoverPassword',
  components: {
    PasswordDetails
  }
})
export default class RecoverPassword extends Vue {
  domain: string = ''
  username: string = ''
  newPassword: string = ''
  newPasswordConfirmation: string = ''
  recoveryPin: string = ''
  recoveryMediaOptions: { [key: string]: string | number } = {}
  media: string = ''
  recoveryPinSent: boolean = false
  recoveryPINReceived: boolean = false
  passwordUpdated: boolean = false
  feedbackValues: string = ''
  feedbackPIN: string = ''
  feedbackPassword: string = ''
  currentStep: number = 0

  validatingCredentials: boolean = false
  sendingRecoveryPin: boolean = false
  updatingPassword: boolean = false

  PINCounterDown: number = 0
  PINCounter: number = 60
  showPassword: boolean = false
  showPasswordConfirmation: boolean = false
  loginCounterDown: number = 0
  loginCounter: number = 10
  domainSecurityLevel: number = DomainSecurityLevel.HIGH

  recoveryValidMedias: string[] = [
    'email',
    'emailExtra',
    'phone',
    'phoneExtra'
  ]

  /** Validates if the domain and username were filled */
  get isDomainAndUsername () : boolean {
    return this.domain.length > 0 && this.username.length > 0
  }

  /** Validates if the domain, username, PIN and password were filled */
  get existPinAndPassword () : boolean {
    return this.isDomainAndUsername &&
      this.recoveryPin.length > 0 &&
      this.newPassword.length > 0
  }

  get routeRecoveryReasonParam (): RecoveryReasons | undefined {
    return this.$route.params.recoveryReason as RecoveryReasons | undefined
  }

  get isExpirationPwd () {
    return this.routeRecoveryReasonParam === 'expirationPwd'
  }
  get isFirstLogin () {
    return this.routeRecoveryReasonParam === 'fisrtLogin'
  }
  get isRecoveryPwd () {
    return this.routeRecoveryReasonParam === 'recoveryPassword'
  }

  get recoveryTitle () {
    if (this.isExpirationPwd || this.isFirstLogin) {
      return 'Cambio de contraseña'
    }
    return 'Recuperación de contraseña'
  }

  get recoveryMediaTitle () {
    if (this.isExpirationPwd || this.isFirstLogin) {
      return 'Medios de actualización'
    }
    return 'Medios de recuperación'
  }

  get recoveryMediaOptionsForRadioGroup () {
    return Object.entries(this.recoveryMediaOptions)
      .map(([value, text]) => (
        { value, text }
      ))
  }

  get recoveryPasswordDetails () {
    return RecoveryPasswordDetails[this.domainSecurityLevel]
  }

  /**
   * Gets the recovery medias by domain and username.
   */
  async getRecoveryPasswordMedias () {
    this.validatingCredentials = true
    this.recoveryMediaOptions = {}
    try {
      const recoveryPasswordMedias = await RecoveryPasswordActions.getRecoveryPasswordMedias(this.domain, this.username)
      const { domainSecurityLevel, ...recoveryMediaOptions } = recoveryPasswordMedias
      this.domainSecurityLevel = domainSecurityLevel
      this.recoveryMediaOptions = recoveryMediaOptions
      setTimeout(() => {
        this.setCurrentStep(1)
      }, 0)
    } catch (error: any) {
      this.manageFeedbackError(APIErrorFormat.getInstance(error.response.data))
    } finally {
      this.validatingCredentials = false
    }
  }

  /**
   * Process the request to get a PIN to update the password.
   */
  async requestRecoveryPasswordPin () {
    this.sendingRecoveryPin = true
    try {
      await RecoveryPasswordActions.requestRecoveryPasswordPin(this.domain, this.username, this.transformMediaRecovery(this.media))
      this.recoveryPinSent = true
      this.counterStart(this.PINCounter, this.setPINCounter)
      this.recoveryPINReceived = true
      setTimeout(() => {
        this.setCurrentStep(2)
      }, 0)
    } catch (error: any) {
      this.manageFeedbackError(APIErrorFormat.getInstance(error.response.data))
    } finally {
      this.sendingRecoveryPin = false
    }
  }

  /**
   * Timer for manages ranges of N seconds
   *
   * @param counter N seconds for timer
   * @param setCounter function that updates the counter value
   */
  counterStart (counter: number, setCounter: any) {
    setCounter(counter)
    setTimeout(() => {
      if (counter > 0) {
        this.counterStart(counter - 1, setCounter)
      }
    }, 1000)
  }

  /**
   * Sets the PINCounterDown variable
   *
   * @param value to set
   */
  setPINCounter (value: number) {
    this.PINCounterDown = value
  }

  /**
   * Sets the LoginCounterDown variable
   *
   * @param value to set
   */
  setLoginCounter (value: number) {
    this.loginCounterDown = value
  }

  /**
   * Validates the requirements for the new password
   */
  validatePasswordRequirements () {
    if (this.newPassword.length > 0) {
      if (this.domainSecurityLevel === DomainSecurityLevel.LOW) {
        return new RegExp(PASSWORD_REGEXP.low_password).test(this.newPassword)
      } else if (this.domainSecurityLevel === DomainSecurityLevel.MEDIUM) {
        return new RegExp(PASSWORD_REGEXP.medium_password).test(this.newPassword)
      } else {
        return new RegExp(PASSWORD_REGEXP.high_password).test(this.newPassword)
      }
    }
    return null
  }

  /**
   * Validates that the new password and its confirmation are the same
   */
  validatePaswwordConfirmation () {
    return this.newPassword.length && this.newPasswordConfirmation.length
      ? this.newPassword === this.newPasswordConfirmation
      : null
  }

  /**
   * To change the input type for the new password
   */
  get passwordHide () {
    return this.showPassword ? 'text' : 'password'
  }

  /**
   * To change the input type for the confirmation password
   */
  get passwordConfirmationHide () {
    return this.showPasswordConfirmation ? 'text' : 'password'
  }

  /**
   * Changes the value for the input password type
   *
   * @param updateValue function to set the flag value
   * @param currentValue function to get the current value flag
   */
  changeShowPassword (updateValue: any, currentValue: any) {
    updateValue(!currentValue())
  }

  /**
   * Sets the showPassword value
   *
   * @param value to set
   */
  setShowPassword (value: boolean) {
    this.showPassword = value
  }

  /**
   * @returns the showPassword value
   */
  getShowPassword () {
    return this.showPassword
  }
  /**
   * Sets the showPasswordConfirmation value
   *
   * @param value to set
   */
  setShowPasswordConfirmation (value: boolean) {
    this.showPasswordConfirmation = value
  }
  /**
   * @returns the showPasswordConfirmation value
   */
  getShowPasswordConfirmation () {
    return this.showPasswordConfirmation
  }
  /**
   * Updates the new password for the user.
   */
  async updatePassword () {
    this.updatingPassword = true
    try {
      const params = {
        domain: this.domain,
        user: this.username,
        pin: this.recoveryPin,
        newPassword: this.newPassword
      }
      await RecoveryPasswordActions.updatePassword(params)
      this.passwordUpdated = true
      this.loginCounterStart()
      this.reditectToLoginContinue()
    } catch (error: any) {
      this.manageFeedbackError(APIErrorFormat.getInstance(error.response.data))
    } finally {
      this.updatingPassword = false
    }
  }

  loginCounterStart () {
    this.counterStart(this.loginCounter, this.setLoginCounter)
  }

  reditectToLoginContinue () {
    setTimeout(() => {
      this.redirectToLogin()
    }, this.loginCounter * 1000)
  }

  /**
   * Manages the error recovery password massages and translate them into feedback
   *
   * @param error response from api
   */
  manageFeedbackError (error: APIErrorFormat) {
    let errorCode
    let originalErrorDescription = 'Error'
    if (error.isNewErrorFormat()) {
      errorCode = error.errors[0].code
      originalErrorDescription = error.errors[0].description
    }
    if (errorCode !== undefined) {
      switch (errorCode) {
        case 3240:
          this.setFeedbackValuesMessage(originalErrorDescription)
          break
        case 3242:
        case 3230:
          this.setFeedbackPINMessage(originalErrorDescription)
          break
        case 2013:
        case 2014:
        case 2016:
          this.setFeedbackPasswordMessage(originalErrorDescription)
          break
      }
    }
  }

  /** Sets the value for the credentials feedback. */
  setFeedbackValuesMessage (msg: string) {
    this.feedbackValues = msg
  }

  /** Sets the value for request PIN feedback. */
  setFeedbackPINMessage (msg: string) {
    this.feedbackPIN = msg
  }

  /** Sets the value for the update password feedback. */
  setFeedbackPasswordMessage (msg: string) {
    this.feedbackPassword = msg
  }

  /**
   * Changes the current active tab
   * @param index of the tab to change
   */
  setCurrentStep (index: number) {
    this.currentStep = index
  }

  /** Changes to the last tab to update the password. */
  pinReceivedContinue () {
    this.recoveryPINReceived = true
    setTimeout(() => {
      this.setCurrentStep(2)
    }, 300)
  }
  /**
   * Translate the recovery media
   *
   * @param media to translate
   */
  mediaTranslate (media: string) {
    let mediaTranslate = ''
    switch (media) {
      case 'email':
        mediaTranslate = 'Correo'
        break
      case 'phone':
        mediaTranslate = 'Teléfono'
        break
      case 'emailExtra':
        mediaTranslate = 'Correo extra'
        break
      case 'phoneExtra':
        mediaTranslate = 'Teléfono extra'
        break
      default:
        mediaTranslate = 'Medio no reconocido'
        break
    }
    return mediaTranslate
  }
  /**
   * Translate the recovery media value for send it in the pin request
   *
   * @param media recovery to transform
   */
  transformMediaRecovery (media: string) {
    let mediaTranslate = ''
    switch (media) {
      case 'email':
        mediaTranslate = 'email'
        break
      case 'phone':
        mediaTranslate = 'phone'
        break
      case 'emailExtra':
        mediaTranslate = 'email_extra'
        break
      case 'phoneExtra':
        mediaTranslate = 'phone_extra'
        break
      default:
        mediaTranslate = 'none'
        break
    }
    return mediaTranslate
  }
  /**
   * Watchs the domain value change and clears all the current values
   * to update the password
   */
  @Watch('domain')
  onChangeDomain () {
    this.clearAllRecovery()
  }

  /**
   * Watchs the username value change and clears all the current values
   * to update the password
   */
  @Watch('username')
  onChangeUser () {
    this.clearAllRecovery()
  }

  /** @returns if there are some recovery media. */
  get existsRecoveryMedia () {
    return Object.keys(this.recoveryMediaOptions).length > 0
  }
  /** @returns the value of the recoveryPINReceived */
  get isRecoveryPINReceived () {
    return this.recoveryPINReceived
  }
  /** Clears all the current values used to update the user password. */
  clearAllRecovery () {
    this.newPassword = ''
    this.recoveryPin = ''
    this.recoveryMediaOptions = {}
    this.media = ''
    this.recoveryPinSent = false
    this.recoveryPINReceived = false
    this.passwordUpdated = false
    this.feedbackValues = ''
    this.feedbackPIN = ''
    this.feedbackPassword = ''
    this.setCurrentStep(0)
  }
  /** Redirecto to the login view */
  redirectToLogin () {
    if (this.$route.name !== 'login') {
      router.push({ name: 'login' })
        .catch((error) => {
          LogService.error('Error on redirect to login. ' + error)
        })
    }
  }
}
